import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Heart, QrCode, X } from "lucide-react";
import { sharedApi } from "@/lib/api";

/**
 * Store Follow (backend landed) — shared contract:
 *   GET    /engagement/follows/mine                → {stores:[...], total}
 *   POST   /engagement/vendors/{id}/follow         (accepts evd_ or eqr_ id)
 *   DELETE /engagement/vendors/{id}/follow         → {following:false}
 * All calls carry ?platform=nz_classified + X-App-Platform.
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

function storeName(s) {
  return s.business_name || "Store";
}
function storeQr(s) {
  return s.qr_code_id || null;
}
function storeKey(s) {
  return s.vendor_id || s.follow_id || storeQr(s);
}

export function useMyFollows(enabled = true) {
  return useQuery({
    queryKey: ["my-follows"],
    enabled,
    retry: 0,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/follows/mine", OPTS)).data,
  });
}

function useFollowToggle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vendorRef, following }) => {
      const url = `/classifieds/engagement/vendors/${vendorRef}/follow`;
      if (following) return (await sharedApi.delete(url, OPTS)).data;
      return (await sharedApi.post(url, {}, OPTS)).data;
    },
    onSuccess: (_data, { following, name }) => {
      toast.success(following ? "Unfollowed." : `Following ${name || "store"}.`);
      qc.invalidateQueries({ queryKey: ["my-follows"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" && d.trim() ? d : "Couldn't update your follows");
    },
  });
}

/** Heart toggle for a single store (used on visit cards).
 *  Iter-336: vendorRef MUST be the evd_ vendor id — the backend rejects
 *  qr codes / short codes / names on POST. */
export function FollowStoreButton({ vendorRef, name, followedIds, testid }) {
  const toggle = useFollowToggle();
  if (!vendorRef) return null;
  const following = followedIds?.has(vendorRef) || false;
  return (
    <button
      type="button"
      data-testid={testid}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate({ vendorRef, following, name })}
      className={`inline-flex items-center gap-1.5 font-semibold transition-colors disabled:opacity-60 ${
        following ? "text-rose-600 hover:text-rose-700" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      <Heart className={`w-3.5 h-3.5 ${following ? "fill-current" : ""}`} />
      {following ? "Following" : "Follow store"}
    </button>
  );
}

/** "Stores you follow" chip strip for /my/visits. Renders nothing while empty. */
export function FollowedStoresStrip({ followsQ, streakByVendor = {} }) {
  const toggle = useFollowToggle();
  const stores = followsQ?.data?.stores || [];
  if (followsQ?.isLoading || followsQ?.isError || stores.length === 0) return null;
  return (
    <div className="mt-8" data-testid="my-follows-strip">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
        Stores you follow
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {stores.map((s) => {
          const qr = storeQr(s);
          const ref = s.vendor_id || storeKey(s);
          const inactive = s.active === false;
          return (
            <li
              key={storeKey(s)}
              className={`inline-flex items-center gap-2 rounded-full bg-white shadow-card border pl-3.5 pr-2 py-1.5 text-sm ${
                inactive ? "border-slate-200 opacity-55" : "border-slate-200"
              }`}
              data-testid={`my-follows-chip-${storeKey(s)}`}
            >
              {/* Iter-337 — show the store's logo when the contract provides one */}
              {s.logo && (
                <img
                  src={s.logo}
                  alt=""
                  className="w-5 h-5 -ml-1 rounded-full object-cover border border-slate-200"
                />
              )}
              <span className={`font-semibold ${inactive ? "text-slate-500" : "text-slate-800"}`}>{storeName(s)}</span>
              {/* Iter-356 — server-canonical streak badge, display only */}
              {s.vendor_id && streakByVendor[s.vendor_id] && (
                <span
                  data-testid={`my-follows-streak-${storeKey(s)}`}
                  className="rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px] font-bold px-1.5 py-0.5"
                >
                  {streakByVendor[s.vendor_id]}
                </span>
              )}
              {inactive ? (
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400" data-testid={`my-follows-inactive-${storeKey(s)}`}>
                  Unavailable
                </span>
              ) : (
                qr && (
                  <Link
                    to={`/engage/${qr}`}
                    data-testid={`my-follows-visit-${storeKey(s)}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
                  >
                    <QrCode className="w-3 h-3" /> Visit
                  </Link>
                )
              )}
              <button
                type="button"
                aria-label={`Unfollow ${storeName(s)}`}
                data-testid={`my-follows-unfollow-${storeKey(s)}`}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ vendorRef: ref, following: true, name: storeName(s) })}
                className="grid place-items-center w-5 h-5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-60"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
